// Trivia Night — the shapes and numbers the server rules (trivia.ts) and the
// client views share. No question data in here: the browser imports this
// file, and the question bank (with the answers) must never reach clients.

import { RuleError, type GameDef } from './types.ts';

export const TRIVIA_CATEGORIES = [
  { id: 'general', label: 'General Knowledge' },
  { id: 'music', label: 'Music' },
  { id: 'hiphop', label: 'Hip-Hop & R&B' },
  { id: 'movies', label: 'Movies & TV' },
  { id: 'sports', label: 'Sports' },
  { id: 'science', label: 'Science & Nature' },
  { id: 'history', label: 'History' },
  { id: 'geography', label: 'Geography' },
  { id: 'blackhistory', label: 'Black History & Culture' },
  { id: 'food', label: 'Food & Drink' },
  { id: 'popculture', label: 'Pop Culture & Tech' },
] as const;
export type TriviaCategory = (typeof TRIVIA_CATEGORIES)[number]['id'];
export const CATEGORY_IDS: string[] = TRIVIA_CATEGORIES.map((c) => c.id);
export const categoryLabel = (id: string) => TRIVIA_CATEGORIES.find((c) => c.id === id)?.label ?? id;

export type TriviaMode = 'classic' | 'buzzer';
export const COUNTS = [10, 15, 20] as const;
export const TIMERS = [15, 20, 30] as const;

export interface TriviaOptions {
  /** Empty = every category (a mix). */
  categories: string[];
  count: number;
  /** Seconds per question. */
  timer: number;
  mode: TriviaMode;
}
export const DEFAULT_OPTIONS: TriviaOptions = { categories: [], count: 10, timer: 20, mode: 'classic' };

export const MAX_PLAYERS = 20;
/** Private row nobody owns: the question queue, the answer, everyone's picks. */
export const SECRET_SEAT = 99;

// ---- timing (ms) -----------------------------------------------------------
export const INTRO_MS = 5_000;
export const REVEAL_MS = 7_000;
export const SCORES_MS = 6_000;
/** Standings get their own screen after every Nth question (not after the last). */
export const SCORES_EVERY = 5;
/** Answers that left a phone just before the buzzer still count. */
export const GRACE_MS = 700;

// ---- scoring ----------------------------------------------------------------
/** Classic: a right answer is worth 500–1000, more the faster it came. */
export const CLASSIC_MIN = 500;
export const CLASSIC_MAX = 1000;
/** Buzzer: points by place among the right answers; a wrong buzz costs a little. */
export const BUZZER_POINTS = [1000, 800, 650, 500, 400, 300];
export const BUZZER_FLOOR = 250;
export const BUZZER_WRONG = -200;

export function classicPoints(ms: number, timerMs: number): number {
  const f = Math.min(1, Math.max(0, ms / timerMs));
  return Math.round(CLASSIC_MAX - (CLASSIC_MAX - CLASSIC_MIN) * f);
}
export function buzzerPoints(place: number): number {
  return place < BUZZER_POINTS.length ? BUZZER_POINTS[place] : BUZZER_FLOOR;
}

/** Validate a lobby options patch (untrusted client JSON). */
export function configureTrivia(current: TriviaOptions | null, patch: unknown): TriviaOptions {
  const cur = { ...DEFAULT_OPTIONS, ...(current ?? {}) };
  if (!patch || typeof patch !== 'object' || Array.isArray(patch)) throw new RuleError('bad options');
  const p = patch as Record<string, unknown>;
  const out: TriviaOptions = { ...cur, categories: [...cur.categories] };
  if ('categories' in p) {
    if (!Array.isArray(p.categories) || p.categories.length > CATEGORY_IDS.length) throw new RuleError('bad categories');
    const list = p.categories.map(String);
    if (list.some((c) => !CATEGORY_IDS.includes(c))) throw new RuleError('unknown category');
    // Every category picked = the mix (empty).
    const uniq = CATEGORY_IDS.filter((c) => list.includes(c));
    out.categories = uniq.length === CATEGORY_IDS.length ? [] : uniq;
  }
  if ('count' in p) {
    const n = Number(p.count);
    if (!(COUNTS as readonly number[]).includes(n)) throw new RuleError('count must be 10, 15 or 20');
    out.count = n;
  }
  if ('timer' in p) {
    const n = Number(p.timer);
    if (!(TIMERS as readonly number[]).includes(n)) throw new RuleError('timer must be 15, 20 or 30');
    out.timer = n;
  }
  if ('mode' in p) {
    if (p.mode !== 'classic' && p.mode !== 'buzzer') throw new RuleError('mode must be classic or buzzer');
    out.mode = p.mode;
  }
  return out;
}

// ---- public state -----------------------------------------------------------

export type TriviaPhase = 'intro' | 'question' | 'reveal' | 'scores' | 'over';

/** The question as everyone sees it — never which choice is right. */
export interface PublicQuestion { text: string; choices: string[]; category: string; difficulty: string }

export interface TriviaReveal {
  /** Index of the right choice. Only present once the question has closed. */
  correct: number;
  /** seat → the choice they picked (seats that answered). */
  picks: Record<number, number>;
  /** seat → points this question (right, wrong buzz…). */
  gains: Record<number, number>;
  /** Fastest right answer. */
  fastest: { seat: number; ms: number } | null;
  /** How many picked each choice. */
  counts: number[];
  /** Thrown out by the host (no points). */
  voided?: boolean;
}

export interface TriviaState {
  phase: TriviaPhase;
  mode: TriviaMode;
  timerMs: number;
  total: number;
  /** 1-based question number (0 during the intro). */
  qNo: number;
  categories: string[];
  question: PublicQuestion | null;
  /** Server time the question went up. */
  askedAt: number | null;
  /** Server time the current phase ends (question countdown, reveal pause…). */
  endsAt: number | null;
  /** Server time of the step that produced this state (clients anchor countdowns to it — no clock skew). */
  at: number;
  /** Set while the host has paused: ms that were left on the clock. */
  pausedLeft: number | null;
  /** Seats that have locked in an answer to the current question (not what they picked). */
  answered: number[];
  reveal: TriviaReveal | null;
  /** seat → total points. */
  scores: Record<number, number>;
  /** seat → right answers. */
  right: Record<number, number>;
  /** seat → display name (kept so the final podium survives someone leaving). */
  names: Record<number, string>;
  /** User ids the host removed from this game (they can't rejoin it). */
  kicked: string[];
}

/** Each player's own row: what they picked for which question. */
export interface TriviaSeatPriv { qNo: number; choice: number }
/** The host's row: the right answer to the current question. */
export interface TriviaHostPriv { qNo: number; answer: number }

export type TriviaMove =
  | { type: 'answer'; qNo: number; choice: number }
  | { type: 'pause' } | { type: 'resume' }
  | { type: 'skip' } | { type: 'reveal' } | { type: 'end' }
  | { type: 'kick'; seat: number };

/** Standings: seats with a score, best first (ties: more right answers, then seat). */
export function standings(g: TriviaState, seats?: (unknown | null)[]): { seat: number; name: string; score: number; right: number; rank: number }[] {
  const list = Object.keys(g.scores).map(Number)
    .filter((s) => !seats || seats[s])
    .map((seat) => ({ seat, name: g.names[seat] ?? `Player ${seat + 1}`, score: g.scores[seat] ?? 0, right: g.right[seat] ?? 0, rank: 0 }))
    .sort((a, b) => b.score - a.score || b.right - a.right || a.seat - b.seat);
  list.forEach((r, i) => { r.rank = i > 0 && list[i - 1].score === r.score ? list[i - 1].rank : i + 1; });
  return list;
}

export type TriviaGameDef = GameDef<TriviaState, TriviaMove>;
